import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './LoginPage.css';

// Import assets for carousel
import evAuto from '../assets/ev-auto-purchase.png';
import evThree from '../assets/ev-three-wheeler.png';
import twoWheeler from '../assets/two-wheeler-bike.png';

function LoginPage() {
  const navigate = useNavigate();

  // Login method state
  const [loginMethod, setLoginMethod] = useState('password'); // 'password' or 'otp'

  // Carousel State
  const [currentSlide, setCurrentSlide] = useState(0);
  const slides = [
    {
      image: evAuto,
      title: "Empowering Your Journey",
      subtitle: "Seamless financial solutions for your electric auto aspirations."
    },
    {
      image: evThree,
      title: "Sustainable Growth",
      subtitle: "Financing the future of eco-friendly transportation."
    },
    {
      image: twoWheeler,
      title: "Ride with Confidence",
      subtitle: "Accessible two-wheeler loans for every family."
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  // Password login states
  const [userId, setUserId] = useState(''); // Can be email or phone
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // OTP login states
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState(['', '', '', '']);
  const [showOtpInput, setShowOtpInput] = useState(false);

  // Common states
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [otpTimer, setOtpTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);

  // Clear error when user interacts
  useEffect(() => {
    setErrorMsg('');
  }, [userId, password, phoneNumber, otp, loginMethod]);

  // Resend Timer logic (60 seconds)
  useEffect(() => {
    let interval = null;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    } else if (resendTimer === 0 && showOtpInput) {
      setCanResend(true);
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [resendTimer, showOtpInput]);

  // OTP Expiry Timer logic (90 seconds)
  useEffect(() => {
    let interval = null;
    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer((prev) => prev - 1);
      }, 1000);
    } else if (otpTimer === 0 && showOtpInput) {
      // OTP has expired
      setErrorMsg('OTP has expired. Please request a new one.');
      setShowOtpInput(false);
      setOtp(['', '', '', '']);
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [otpTimer, showOtpInput]);

  // API Base URL
  const API_URL = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    console.log('🌐 [FRONTEND-DIAG] App location:', window.location.href);
    console.log('🔗 [FRONTEND-DIAG] API URL set to:', API_URL || '(current domain)');
  }, [API_URL]);

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    console.log('🔐 [FRONTEND-LOGIN] Initiating password login for:', userId);
    if (userId && password && termsAccepted) {
      setIsLoading(true);

      try {
        const fetchUrl = `${API_URL}/api/auth/login`;
        console.log(`🌐 [FRONTEND-LOGIN] Fetching: ${fetchUrl}`);
        const response = await fetch(fetchUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ userId, password }),
        });

        console.log(`📡 [FRONTEND-LOGIN] Response status: ${response.status}`);
        const data = await response.json();
        console.log('📦 [FRONTEND-LOGIN] Response data:', data);

        if (response.ok && data.success) {
          console.log('✅ [FRONTEND-LOGIN] Success! Setting session...');
          const sessionData = {
            userId: data.user.id,
            phone: data.user.phone,
            email: data.user.email,
            name: data.user.name,
            role: data.user.role,
            loginMethod: 'password',
            loginTime: Date.now(),
            expiresAt: Date.now() + (30 * 60 * 1000) // 30 minutes
          };

          localStorage.setItem('userSession', JSON.stringify(sessionData));
          console.log('🚀 [FRONTEND-LOGIN] Navigating to dashboard...');
          navigate('/dashboard', { replace: true });
        } else {
          console.warn('⚠️ [FRONTEND-LOGIN] Login failure:', data.message);
          setErrorMsg(data.message || 'Login failed. Please check your credentials.');
        }
      } catch (error) {
        console.error('❌ [FRONTEND-LOGIN] Error:', error);
        setErrorMsg('Network error. Please check your connection and try again.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    console.log('📲 [FRONTEND-OTP-REQ] Initiating OTP request for:', phoneNumber);
    if (phoneNumber.length === 10 && termsAccepted) {
      setIsLoading(true);

      try {
        const fetchUrl = `${API_URL}/api/auth/request-otp`;
        console.log(`🌐 [FRONTEND-OTP-REQ] Fetching: ${fetchUrl}`);
        const response = await fetch(fetchUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ phone: phoneNumber }),
        });

        console.log(`📡 [FRONTEND-OTP-REQ] Response status: ${response.status}`);
        const data = await response.json();
        console.log('📦 [FRONTEND-OTP-REQ] Response data:', data);

        if (response.ok && data.success) {
          console.log('✅ [FRONTEND-OTP-REQ] Success! Showing OTP input...');
          setShowOtpInput(true);
          setResendTimer(60); // 1 minute resend timer
          setOtpTimer(90); // 90 seconds OTP validity
          setCanResend(false);
        } else {
          console.warn('⚠️ [FRONTEND-OTP-REQ] Failure:', data.message);
          setErrorMsg(data.message || 'Failed to send OTP. Please try again.');
        }
      } catch (error) {
        console.error('❌ [FRONTEND-OTP-REQ] Error:', error);
        setErrorMsg('Network error. Please check your connection and try again.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  // OTP change handler
  const handleOtpChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Auto-focus next input
      if (value && index < 3) {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  const handleOtpLogin = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    const otpValue = otp.join('');
    console.log('🔑 [FRONTEND-OTP-VERIFY] Initiating OTP verification for:', phoneNumber);

    if (otpValue.length === 4) {
      setIsLoading(true);

      try {
        const fetchUrl = `${API_URL}/api/auth/verify-otp`;
        console.log(`🌐 [FRONTEND-OTP-VERIFY] Fetching: ${fetchUrl}`);
        const response = await fetch(fetchUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            phone: phoneNumber,
            otp: otpValue
          }),
        });

        console.log(`📡 [FRONTEND-OTP-VERIFY] Response status: ${response.status}`);
        const data = await response.json();
        console.log('📦 [FRONTEND-OTP-VERIFY] Response data:', data);

        if (response.ok && data.success) {
          console.log('✅ [FRONTEND-OTP-VERIFY] Success! Setting session...');
          const sessionData = {
            userId: data.user.id,
            phone: data.user.phone,
            email: data.user.email,
            name: data.user.name,
            role: data.user.role,
            loginMethod: 'otp',
            loginTime: Date.now(),
            expiresAt: Date.now() + (30 * 60 * 1000) // 30 minutes
          };

          localStorage.setItem('userSession', JSON.stringify(sessionData));
          console.log('🚀 [FRONTEND-OTP-VERIFY] Navigating to dashboard...');
          navigate('/dashboard', { replace: true });
        } else {
          console.warn('⚠️ [FRONTEND-OTP-VERIFY] Failure:', data.message);
          setErrorMsg(data.message || 'Invalid OTP. Please try again.');
        }
      } catch (error) {
        console.error('❌ [FRONTEND-OTP-VERIFY] Error:', error);
        setErrorMsg('Network error. Please check your connection and try again.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Switch login method
  const switchLoginMethod = (method) => {
    setLoginMethod(method);
    setShowOtpInput(false);
    setOtp(['', '', '', '']);
    setErrorMsg('');
  };

  return (
    <div className="login-page">
      <div className="login-container">
        {/* Left Section - Login Form */}
        <div className="login-form-section">
          <Link to="/" className="login-logo">
            <img src={`${process.env.PUBLIC_URL || ''}/Png-01.png`} alt="E-Fin Logo" />
          </Link>

          <div className="login-content">
            <h1>Welcome to E-Fin</h1>
            <p className="login-subtitle">
              {showOtpInput
                ? 'Enter the verification code sent to your mobile'
                : 'Login to your account to get started'}
            </p>

            {errorMsg && (
              <div className="login-error-container" style={{
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid #ef4444',
                color: '#ef4444',
                padding: '0.8rem',
                borderRadius: '8px',
                marginBottom: '1.5rem',
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                animation: 'shake 0.5s ease-in-out'
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                {errorMsg}
              </div>
            )}

            {/* Login Method Tabs */}
            {!showOtpInput && (
              <div className="login-tabs">
                <button
                  type="button"
                  className={`tab-btn ${loginMethod === 'password' ? 'active' : ''}`}
                  onClick={() => switchLoginMethod('password')}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                  </svg>
                  Password
                </button>
                <button
                  type="button"
                  className={`tab-btn ${loginMethod === 'otp' ? 'active' : ''}`}
                  onClick={() => switchLoginMethod('otp')}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                  </svg>
                  OTP
                </button>
              </div>
            )}

            {/* Password Login Form */}
            {loginMethod === 'password' && !showOtpInput && (
              <form className="auth-form" onSubmit={handlePasswordLogin}>
                <div className="form-group">
                  <label htmlFor="userId">Email or Phone Number</label>
                  <input
                    id="userId"
                    type="text"
                    placeholder="Enter email or 10-digit mobile number"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <div className="password-input-wrapper">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="toggle-password"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                          <line x1="1" y1="1" x2="23" y2="23"></line>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                      )}
                    </button>
                  </div>
                  <Link to="/forgot-password" title="Forgot Password" className="forgot-password-link">
                    Forgot password?
                  </Link>
                </div>

                <div className="form-checkbox">
                  <input
                    type="checkbox"
                    id="terms-password"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    required
                  />
                  <label htmlFor="terms-password">
                    I agree to the <Link to="/privacy">Terms & Conditions</Link> and{' '}
                    <Link to="/privacy">Privacy Policy</Link>
                  </label>
                </div>

                <button
                  type="submit"
                  className="submit-btn"
                  disabled={!userId || !password || !termsAccepted || isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner"></span> Logging in...
                    </>
                  ) : (
                    'Login'
                  )}
                </button>
              </form>
            )}

            {/* OTP Login Form */}
            {loginMethod === 'otp' && !showOtpInput && (
              <form className="auth-form" onSubmit={handleRequestOtp}>
                <div className="form-group">
                  <label htmlFor="phone">Mobile Number</label>
                  <div className="input-wrapper">
                    <span className="input-prefix">+91</span>
                    <input
                      id="phone"
                      type="tel"
                      placeholder="Enter 10-digit mobile number"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      maxLength="10"
                      required
                    />
                  </div>
                </div>

                <div className="form-checkbox">
                  <input
                    type="checkbox"
                    id="terms-otp"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    required
                  />
                  <label htmlFor="terms-otp">
                    I agree to the <Link to="/privacy">Terms & Conditions</Link> and{' '}
                    <Link to="/privacy">Privacy Policy</Link>
                  </label>
                </div>

                <button
                  type="submit"
                  className="submit-btn"
                  disabled={phoneNumber.length !== 10 || !termsAccepted || isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner"></span> Sending OTP...
                    </>
                  ) : (
                    'Get OTP'
                  )}
                </button>
              </form>
            )}

            {/* OTP Verification Form */}
            {showOtpInput && (
              <form className="auth-form" onSubmit={handleOtpLogin}>
                <div className="form-group">
                  <label>Enter OTP</label>
                  <div className="otp-inputs">
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        id={`otp-${index}`}
                        type="text"
                        inputMode="numeric"
                        maxLength="1"
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Backspace' && !digit && index > 0) {
                            const prevInput = document.getElementById(`otp-${index - 1}`);
                            if (prevInput) prevInput.focus();
                          }
                        }}
                      />
                    ))}
                  </div>
                  <div className="otp-footer">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', width: '100%' }}>
                      {otpTimer > 0 && (
                        <p className="timer-text" style={{
                          color: otpTimer <= 30 ? '#ef4444' : '#6b7280',
                          fontWeight: otpTimer <= 30 ? '600' : '400',
                          margin: 0
                        }}>
                          OTP expires in <span>{otpTimer}s</span>
                        </p>
                      )}
                      {resendTimer > 0 ? (
                        <p className="timer-text" style={{ margin: 0 }}>
                          Resend OTP in <span>{resendTimer}s</span>
                        </p>
                      ) : (
                        <button
                          type="button"
                          className="resend-btn"
                          onClick={handleRequestOtp}
                          disabled={!canResend || isLoading}
                        >
                          Resend OTP
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  className="submit-btn"
                  disabled={otp.join('').length !== 4 || isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner"></span> Verifying...
                    </>
                  ) : (
                    'Verify & Login'
                  )}
                </button>

                <button
                  type="button"
                  className="change-number-btn"
                  onClick={() => {
                    setShowOtpInput(false);
                    setOtp(['', '', '', '']);
                    setErrorMsg('');
                  }}
                >
                  Change Mobile Number
                </button>
              </form>
            )}
          </div>

          <p className="login-footer">
            New to E-Fin? <Link to="/support/apply">Create an account</Link>
          </p>
        </div>

        {/* Right Section - Visual */}
        <div className="login-visual-section">
          <div className="carousel-track">
            {slides.map((slide, index) => (
              <div
                key={index}
                className={`carousel-slide ${index === currentSlide ? 'active' : ''}`}
                style={{ backgroundImage: `url(${slide.image})` }}
              >
                <div className="slide-content">
                  <h2>{slide.title}</h2>
                  <p>{slide.subtitle}</p>
                </div>
                <div className="slide-overlay"></div>
              </div>
            ))}
          </div>
          <div className="carousel-indicators">
            {slides.map((_, index) => (
              <button
                key={index}
                className={`indicator ${index === currentSlide ? 'active' : ''}`}
                onClick={() => setCurrentSlide(index)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
